type EnvValue = string | null | undefined;
type EnvLike = Record<string, EnvValue> | undefined;

type GoogleCredentialResponse = {
  credential?: string;
};

export type PreferredAuthMode = 'phone' | 'qr';

type GoogleIdentityApi = {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    use_fedcm_for_button?: boolean;
    button_auto_select?: boolean;
  }) => void;
  prompt: (momentListener?: (notification: unknown) => void) => void;
  renderButton: (
    container: HTMLElement,
    options: Record<string, string | number>,
  ) => void;
};

type GoogleSdkLike = {
  accounts?: {
    id?: Partial<GoogleIdentityApi>;
  };
} | undefined;

export type GooglePhoneLogin = {
  phone: string;
  code: string;
  password?: string;
};

function normalizeGoogleOauthClientId(value: EnvValue): string {
  const normalized = value?.trim() || '';
  if (!normalized || normalized === 'null' || normalized === 'undefined') {
    return '';
  }
  return normalized;
}

export function resolveGoogleOauthClientId(env?: EnvLike): string {
  return (
    normalizeGoogleOauthClientId(env?.GOOGLE_OAUTH_CLIENT_ID)
    || normalizeGoogleOauthClientId(env?.EASYCEO_GOOGLE_OAUTH_CLIENT_ID)
    || normalizeGoogleOauthClientId(env?.RUNAI_GOOGLE_OAUTH_CLIENT_ID)
  );
}

const PUBLIC_WEBCHAT_PHONE_AUTH_HOSTS = new Set([
  'run.ceo',
  'public.run.ceo',
  'easyceo.thelightco.net',
]);
const INTERNAL_WEBCHAT_SHELL_HOSTS = new Set([
  'i.run.ceo',
  'app.run.ceo',
  'localhost',
  '127.0.0.1',
]);
const PUBLIC_WEBCHAT_PHONE_AUTH_PATHS = new Set(['/webchat', '/webchat/', '/webchat/index.html']);
const PUBLIC_DOCKER_PREVIEW_SUFFIX = '-docker.i.run.ceo';
const PUBLIC_DOCKER_PREVIEW_PREFIXES = ['ssp', 'sp', 'ss', 's'];
const PUBLIC_ANDROID_HANDOFF_RETURN_SCHEME = 'runai-public://public-handoff';
const PUBLIC_SAAS_WEBCHAT_CONTEXT_KEY = 'run:publicSaasWebchatContext';
const SAAS_GOOGLE_OWNER_AUTH_HOST = 'run.ceo';
const SAAS_GOOGLE_OWNER_AUTH_TARGET_HOSTS = new Set([
  'run.i.run.ceo',
]);
const WORK_GOOGLE_BRIDGE_CALLBACK_HOST = 'i.run.ceo';
const WORK_GOOGLE_BRIDGE_TARGET_HOST = 'run.i.run.ceo';
const WORK_GOOGLE_BRIDGE_TARGET_TENANT_ID = 'run';
const WORK_GOOGLE_BRIDGE_RETURN_TO_PATH = '/work';
const WORK_GOOGLE_BRIDGE_RUN_SOURCE = 'work_google_bridge';
const WORK_GOOGLE_BRIDGE_STATUS = 'consumed';
const UUID_V1_TO_V5_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type WorkGoogleBridgeTransactionTruth = {
  transactionId: string;
  provider: string;
  sourceHost: string;
  callbackHost: string;
  targetHost: string;
  targetTenantId: string;
  returnToPath: string;
  status: string;
};

function isDigitsOnly(value: string): boolean {
  return value.length > 0 && Array.from(value).every((char) => char >= '0' && char <= '9');
}

function isPublicDockerPreviewHost(normalizedHost: string): boolean {
  if (!normalizedHost.endsWith(PUBLIC_DOCKER_PREVIEW_SUFFIX)) return false;
  const lane = normalizedHost.slice(0, -PUBLIC_DOCKER_PREVIEW_SUFFIX.length);
  return PUBLIC_DOCKER_PREVIEW_PREFIXES.some((prefix) => {
    return lane.startsWith(prefix) && isDigitsOnly(lane.slice(prefix.length));
  });
}

function isPublicWebchatHost(hostname: string): boolean {
  const normalizedHost = hostname.trim().toLowerCase();
  return PUBLIC_WEBCHAT_PHONE_AUTH_HOSTS.has(normalizedHost) || isPublicDockerPreviewHost(normalizedHost);
}

function isPublicSaasWebchatContextPath(pathname: string): boolean {
  const normalizedPath = pathname || '/';
  return normalizedPath === '/' || PUBLIC_WEBCHAT_PHONE_AUTH_PATHS.has(normalizedPath);
}

export function isPublicWebchatEntrypoint(
  pathname: string,
  hostname = window.location.hostname,
): boolean {
  const normalizedPath = pathname || '/';

  return isPublicWebchatHost(hostname) && PUBLIC_WEBCHAT_PHONE_AUTH_PATHS.has(normalizedPath);
}

function readSafeSaasReturnTo(search: string): string | undefined {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const searchParams = new URLSearchParams(normalized);
  if (searchParams.get('run_source') !== 'saas') {
    return undefined;
  }
  const raw = searchParams.get('return_to')?.trim();
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) {
    return undefined;
  }
  return raw;
}

function readSaasReturnToPath(search: string): string | undefined {
  const raw = readSafeSaasReturnTo(search);
  if (!raw) {
    return undefined;
  }
  try {
    return new URL(raw, 'https://run.ceo').pathname;
  } catch {
    return undefined;
  }
}

function isSaasWorkReturnTo(search: string): boolean {
  const pathname = readSaasReturnToPath(search);
  return pathname === '/work' || Boolean(pathname?.startsWith('/work/'));
}

function normalizeSaasGoogleOwnerAuthHost(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/:\d+$/, '');
}

function normalizeComparableHost(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/:\d+$/, '');
}

function readTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function isSaasGoogleOwnerAuthTargetHost(hostname: string): boolean {
  const normalizedHost = normalizeSaasGoogleOwnerAuthHost(hostname);
  return SAAS_GOOGLE_OWNER_AUTH_TARGET_HOSTS.has(normalizedHost);
}

function readSafeSaasGoogleOwnerAuthTargetHost(search: string): string | undefined {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const searchParams = new URLSearchParams(normalized);
  const rawHost = searchParams.get('run_target_host')?.trim().toLowerCase();
  if (!rawHost) {
    return undefined;
  }
  if (
    rawHost.includes('/')
    || rawHost.includes('\\')
    || rawHost.includes('@')
    || rawHost.includes(':')
  ) {
    return undefined;
  }
  return isSaasGoogleOwnerAuthTargetHost(rawHost) ? rawHost : undefined;
}

export function buildSaasGoogleAuthBridgeUrl(
  search: string,
  pathname = window.location.pathname,
  hostname = window.location.hostname,
): string | undefined {
  if (!isWorkGoogleBridgeCallback(search, pathname, hostname)) {
    return undefined;
  }

  const bridgeTransactionId = readWorkGoogleBridgeCallbackTransactionId(search, pathname, hostname);
  if (!bridgeTransactionId) {
    return undefined;
  }

  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const searchParams = new URLSearchParams(normalized);
  const targetHost = readTrimmedString(searchParams.get('run_target_host'));
  const targetTenant = readTrimmedString(searchParams.get('run_target_tenant'));
  if (normalizeComparableHost(targetHost) !== WORK_GOOGLE_BRIDGE_TARGET_HOST) {
    return undefined;
  }
  if (targetTenant !== WORK_GOOGLE_BRIDGE_TARGET_TENANT_ID) {
    return undefined;
  }

  const url = new URL('/webchat/', `https://${SAAS_GOOGLE_OWNER_AUTH_HOST}`);
  url.searchParams.set('run_source', 'saas');
  url.searchParams.set('return_to', WORK_GOOGLE_BRIDGE_RETURN_TO_PATH);
  url.searchParams.set('run_target_host', WORK_GOOGLE_BRIDGE_TARGET_HOST);
  return url.toString();
}

export function isWorkGoogleBridgeCallback(
  search: string,
  pathname = window.location.pathname,
  hostname = window.location.hostname,
): boolean {
  if (!PUBLIC_WEBCHAT_PHONE_AUTH_PATHS.has(pathname || '/')) {
    return false;
  }

  return (
    normalizeComparableHost(hostname) === WORK_GOOGLE_BRIDGE_CALLBACK_HOST
    && new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
      .get('run_source') === WORK_GOOGLE_BRIDGE_RUN_SOURCE
  );
}

export function readWorkGoogleBridgeCallbackTransactionId(
  search: string,
  pathname = window.location.pathname,
  hostname = window.location.hostname,
): string | undefined {
  if (!isWorkGoogleBridgeCallback(search, pathname, hostname)) {
    return undefined;
  }

  const bridgeTransactionId = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  ).get('bridgeTransactionId')?.trim();
  if (!bridgeTransactionId || !UUID_V1_TO_V5_PATTERN.test(bridgeTransactionId)) {
    return undefined;
  }

  return bridgeTransactionId;
}

export function readCanonicalWorkGoogleBridgeTransactionTruth(
  payload: unknown,
  bridgeTransactionId: string,
): WorkGoogleBridgeTransactionTruth | undefined {
  if (!UUID_V1_TO_V5_PATTERN.test(bridgeTransactionId)) {
    return undefined;
  }

  const transaction = (
    payload
    && typeof payload === 'object'
    && 'transaction' in payload
    && (payload as { transaction?: unknown }).transaction
    && typeof (payload as { transaction?: unknown }).transaction === 'object'
  )
    ? (payload as { transaction: Partial<WorkGoogleBridgeTransactionTruth> }).transaction
    : undefined;

  if (!transaction) {
    return undefined;
  }

  const normalizedTruth: WorkGoogleBridgeTransactionTruth = {
    transactionId: readTrimmedString(transaction.transactionId),
    provider: readTrimmedString(transaction.provider),
    sourceHost: normalizeComparableHost(readTrimmedString(transaction.sourceHost)),
    callbackHost: normalizeComparableHost(readTrimmedString(transaction.callbackHost)),
    targetHost: normalizeComparableHost(readTrimmedString(transaction.targetHost)),
    targetTenantId: readTrimmedString(transaction.targetTenantId),
    returnToPath: readTrimmedString(transaction.returnToPath),
    status: readTrimmedString(transaction.status),
  };

  if (
    normalizedTruth.transactionId !== bridgeTransactionId
    || normalizedTruth.provider !== 'google'
    || normalizedTruth.sourceHost !== WORK_GOOGLE_BRIDGE_TARGET_HOST
    || normalizedTruth.callbackHost !== WORK_GOOGLE_BRIDGE_CALLBACK_HOST
    || normalizedTruth.targetHost !== WORK_GOOGLE_BRIDGE_TARGET_HOST
    || normalizedTruth.targetTenantId !== WORK_GOOGLE_BRIDGE_TARGET_TENANT_ID
    || normalizedTruth.returnToPath !== WORK_GOOGLE_BRIDGE_RETURN_TO_PATH
    || normalizedTruth.status !== WORK_GOOGLE_BRIDGE_STATUS
  ) {
    return undefined;
  }

  return normalizedTruth;
}

export function buildWorkGoogleBridgeSaasReturnUrl(params: {
  ownerAccessToken: string;
  transaction: WorkGoogleBridgeTransactionTruth;
}): string | undefined {
  const ownerAccessToken = params.ownerAccessToken.trim();
  if (!ownerAccessToken) {
    return undefined;
  }

  const canonicalTruth = readCanonicalWorkGoogleBridgeTransactionTruth(
    { transaction: params.transaction },
    params.transaction.transactionId,
  );
  if (!canonicalTruth) {
    return undefined;
  }

  const url = new URL('/webchat/', `https://${canonicalTruth.targetHost}`);
  url.searchParams.set('run_source', 'saas');
  url.searchParams.set('return_to', WORK_GOOGLE_BRIDGE_RETURN_TO_PATH);
  url.searchParams.set('run_target_host', canonicalTruth.targetHost);
  url.searchParams.set('run_target_tenant', canonicalTruth.targetTenantId);
  url.searchParams.set('ownerAccessToken', ownerAccessToken);
  return url.toString();
}

export function shouldUseSaasGoogleOwnerAuth(
  search: string,
  pathname = window.location.pathname,
  hostname = window.location.hostname,
): boolean {
  const normalizedHost = normalizeSaasGoogleOwnerAuthHost(hostname);
  if (!isSaasWorkReturnTo(search) || !isPublicSaasWebchatContextPath(pathname)) {
    return false;
  }

  if (normalizedHost === SAAS_GOOGLE_OWNER_AUTH_HOST) {
    return Boolean(readSafeSaasGoogleOwnerAuthTargetHost(search));
  }

  return isSaasGoogleOwnerAuthTargetHost(normalizedHost);
}

export function buildSaasGoogleOwnerAuthTargetUrl(params: {
  ownerAccessToken: string;
  search?: string;
  hostname?: string;
}): string | undefined {
  const search = params.search ?? window.location.search;
  const hostname = params.hostname ?? window.location.hostname;
  const ownerAccessToken = params.ownerAccessToken.trim();
  if (!ownerAccessToken || !isSaasWorkReturnTo(search)) {
    return undefined;
  }

  const returnTo = readSafeSaasReturnTo(search);
  const targetHost = readSafeSaasGoogleOwnerAuthTargetHost(search)
    || (
      isSaasGoogleOwnerAuthTargetHost(hostname)
        ? normalizeSaasGoogleOwnerAuthHost(hostname)
        : undefined
    );
  if (!returnTo || !targetHost) {
    return undefined;
  }

  const url = new URL('/webchat/', `https://${targetHost}`);
  url.searchParams.set('run_source', 'saas');
  url.searchParams.set('return_to', returnTo);
  url.searchParams.set('ownerAccessToken', ownerAccessToken);
  return url.toString();
}

export function shouldPreferPhoneAuth(
  search: string,
  pathname = window.location.pathname,
  hostname = window.location.hostname,
): boolean {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const searchParams = new URLSearchParams(normalized);
  const auth = searchParams.get('auth');
  if (auth === 'phone') return true;
  if (auth === 'qr') return false;
  if (
    searchParams.get('run_source') === 'saas'
    && searchParams.get('run_return_to')?.trim() === PUBLIC_ANDROID_HANDOFF_RETURN_SCHEME
  ) {
    return false;
  }
  if (
    searchParams.get('run_source') === 'saas'
    && searchParams.get('ownerAccessToken')?.trim()
    && isPublicWebchatEntrypoint(pathname, hostname)
  ) {
    return false;
  }

  const normalizedPath = pathname || '/';
  return isPublicWebchatHost(hostname) && PUBLIC_WEBCHAT_PHONE_AUTH_PATHS.has(normalizedPath);
}

export function shouldAutoPromptGoogleAuth(
  search: string,
  pathname = window.location.pathname,
  hostname = window.location.hostname,
): boolean {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const searchParams = new URLSearchParams(normalized);
  if (isSaasWorkReturnTo(search) && isPublicSaasWebchatContextPath(pathname)) {
    return false;
  }
  if (
    searchParams.get('run_source') === 'saas'
    && searchParams.get('run_return_to')?.trim() === PUBLIC_ANDROID_HANDOFF_RETURN_SCHEME
    && isPublicWebchatEntrypoint(pathname, hostname)
  ) {
    return false;
  }

  if (
    searchParams.get('run_source') === 'saas'
    && searchParams.get('ownerAccessToken')?.trim()
    && isPublicWebchatEntrypoint(pathname, hostname)
  ) {
    return false;
  }

  return true;
}

export function shouldShowGoogleAuth(
  search: string,
  pathname = window.location.pathname,
  hostname = window.location.hostname,
): boolean {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const searchParams = new URLSearchParams(normalized);

  if (
    searchParams.get('run_source') === 'saas'
    && searchParams.get('ownerAccessToken')?.trim()
    && isPublicWebchatEntrypoint(pathname, hostname)
  ) {
    return false;
  }

  return true;
}

export function isPublicSaasWebchatContext(
  search = window.location.search,
  pathname = window.location.pathname,
  hostname = window.location.hostname,
): boolean {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const searchParams = new URLSearchParams(normalized);
  if (!isPublicWebchatHost(hostname) || !isPublicSaasWebchatContextPath(pathname)) {
    return false;
  }
  if (searchParams.get('run_source') === 'saas') {
    return true;
  }
  try {
    return window.sessionStorage.getItem(PUBLIC_SAAS_WEBCHAT_CONTEXT_KEY) === '1';
  } catch {
    return false;
  }
}

export function markPublicSaasWebchatContext(
  search = window.location.search,
  pathname = window.location.pathname,
  hostname = window.location.hostname,
): boolean {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const searchParams = new URLSearchParams(normalized);
  if (
    searchParams.get('run_source') !== 'saas'
    || !isPublicWebchatHost(hostname)
    || !isPublicSaasWebchatContextPath(pathname)
  ) {
    return false;
  }
  try {
    window.sessionStorage.setItem(PUBLIC_SAAS_WEBCHAT_CONTEXT_KEY, '1');
  } catch {
    return false;
  }
  return true;
}

export function clearPublicSaasWebchatContext() {
  try {
    window.sessionStorage.removeItem(PUBLIC_SAAS_WEBCHAT_CONTEXT_KEY);
  } catch {
    // Ignore storage failures; logout cookie clearing remains the canonical server step.
  }
}

export function hasPendingWebAuthToken(hash: string): boolean {
  return hash.includes('tgWebAuthToken=');
}

export function getPublicWebchatCanonicalPath(
  pathname: string,
  hostname = window.location.hostname,
): string {
  if (!isPublicWebchatEntrypoint(pathname, hostname)) {
    return pathname;
  }

  return '/webchat/';
}

export function shouldHandlePathnameAsDeepLink(
  pathname: string,
  hostname = window.location.hostname,
): boolean {
  if (!pathname || pathname === '/') {
    return false;
  }

  return !isPublicWebchatEntrypoint(pathname, hostname);
}

export function getWebchatDeepLinkCleanupPath(
  hostname = window.location.hostname,
): string {
  const normalizedHost = hostname.trim().toLowerCase();
  if (isPublicWebchatHost(normalizedHost) || INTERNAL_WEBCHAT_SHELL_HOSTS.has(normalizedHost)) {
    return '/webchat/';
  }

  return '/';
}

export function stripPreferredPhoneAuthParam(currentUrl: string): string {
  const url = new URL(currentUrl);
  url.searchParams.delete('auth');
  return `${url.pathname}${url.search}${url.hash}`;
}

export function withPreferredAuthMode(
  currentUrl: string,
  mode?: PreferredAuthMode,
): string {
  const url = new URL(currentUrl);
  if (!isPublicWebchatEntrypoint(url.pathname, url.hostname)) {
    return `${url.pathname}${url.search}${url.hash}`;
  }

  if (mode) {
    url.searchParams.set('auth', mode);
  } else {
    url.searchParams.delete('auth');
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

export function parseGooglePhoneLogin(payload: unknown): GooglePhoneLogin | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const raw = (payload as { phoneLogin?: Partial<GooglePhoneLogin> }).phoneLogin;
  if (!raw?.phone || !raw.code) return undefined;
  return {
    phone: raw.phone,
    code: raw.code,
    ...(raw.password ? { password: raw.password } : {}),
  };
}

function getGoogleIdentityApi(google: GoogleSdkLike): GoogleIdentityApi | undefined {
  const api = google?.accounts?.id;
  if (!api?.initialize || !api.prompt || !api.renderButton) {
    return undefined;
  }
  return api as GoogleIdentityApi;
}

function resolveGoogleButtonWidth(container: HTMLElement): number | undefined {
  const width = Math.round(container.clientWidth || container.getBoundingClientRect().width || 0);
  return width > 0 ? width : undefined;
}

export function mountGoogleSignInButton(params: {
  google: GoogleSdkLike;
  clientId: string;
  container: HTMLElement;
  onCredential: (credential: string) => void;
  autoPrompt: boolean;
  onDebugEvent?: (type: string, detail?: Record<string, unknown>) => void;
}): boolean {
  const clientId = normalizeGoogleOauthClientId(params.clientId);
  const api = getGoogleIdentityApi(params.google);
  if (!clientId || !api) {
    params.onDebugEvent?.('mount_skipped', {
      hasClientId: Boolean(clientId),
      hasSdk: Boolean(api),
    });
    return false;
  }

  params.onDebugEvent?.('initialize', {
    clientIdSuffix: clientId.slice(-12),
  });
  api.initialize({
    client_id: clientId,
    use_fedcm_for_button: true,
    callback: (response) => {
      params.onDebugEvent?.('credential_callback', {
        hasCredential: Boolean(response.credential),
      });
      if (response.credential) {
        params.onCredential(response.credential);
      }
    },
  });

  params.container.innerHTML = '';
  const width = resolveGoogleButtonWidth(params.container) ?? 320;
  params.onDebugEvent?.('render_button', { width });
  api.renderButton(params.container, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    shape: 'rectangular',
    text: 'signin_with',
    width,
    logo_alignment: 'left',
  });

  if (params.autoPrompt) {
    params.onDebugEvent?.('prompt_request');
    api.prompt((notification) => {
      const moment = notification as {
        isDisplayMoment?: () => boolean;
        isDisplayed?: () => boolean;
        isNotDisplayed?: () => boolean;
        getNotDisplayedReason?: () => string;
        isSkippedMoment?: () => boolean;
        getSkippedReason?: () => string;
        isDismissedMoment?: () => boolean;
        getDismissedReason?: () => string;
      };
      params.onDebugEvent?.('prompt_moment', {
        displayed: moment.isDisplayed?.() ?? moment.isDisplayMoment?.() ?? false,
        notDisplayed: moment.isNotDisplayed?.() ?? false,
        notDisplayedReason: moment.getNotDisplayedReason?.(),
        skipped: moment.isSkippedMoment?.() ?? false,
        skippedReason: moment.getSkippedReason?.(),
        dismissed: moment.isDismissedMoment?.() ?? false,
        dismissedReason: moment.getDismissedReason?.(),
      });
    });
  }

  return true;
}
